import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { BuildingService } from '../../services/building.service';
import { EntityModelCustomizationService } from '../../services/entity-model-customization.service';
import {
  DynamicBuildingFilter,
  Purpose,
  SectionDefinitionEntityType,
  TableListPage,
} from '../../models/dynamic-buildings.model';
import { ChecklistItem } from '../../models/checklist-item.model';
import { PermissionsService, EMCType, EmcSearchOutput, EmcOptions, DynamicCardsComponent } from 'shared-lib';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-dynamic-buildings',
  templateUrl: './dynamic-buildings.component.html',
  styleUrls: ['./dynamic-buildings.component.scss'],
})
export class DynamicBuildingsComponent implements OnInit, OnDestroy {
  @ViewChild('dynamicCards') dynamicCards?: DynamicCardsComponent;

  filter: DynamicBuildingFilter = new DynamicBuildingFilter();

  cardEmc: EmcOptions[] = [];
  listPageEmc: EmcOptions[] = [];
  filterEmc: EmcOptions[] = [];

  entityType: EMCType = EMCType.Building;
  purposeCard = Purpose.Card;
  purposeFilter = Purpose.Filter;

  tableListPage: TableListPage = new TableListPage();
  visibleColumns: { label: string; index: number }[] = [];

  pageSize: number = 10;
  currentPage: number = 1;
  count: number = 0;

  checklistItems: ChecklistItem[] = [];
  isFilterWindowOpened: boolean = false;
  utilityId: number = 0;
  hasCimDataIntegration: boolean = false;

  showDownload: boolean = false;

  private ngUnsubscribe = new Subject<void>();

  private validationStatusLabels: Record<number, string> = {
    0: 'Pending',
    1: 'Complete',
    3: 'Assigned',
    4: 'Rejected',
    5: 'Retagged',
  };

  constructor(
    private buildingService: BuildingService,
    private emcService: EntityModelCustomizationService,
    private toastr: ToastrService,
    private router: Router,
    public permissionsService: PermissionsService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '0');
    this.filter.utilityId = this.utilityId;
    this.filter.pageInfo.pageSize = this.pageSize;
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;

    const cachedFilters = localStorage.getItem(`filterDynamicBuildings|${this.utilityId}`);
    if (cachedFilters) {
      const parsed = JSON.parse(cachedFilters);
      this.filter = parsed;
      this.filter.utilityId = this.utilityId;
      this.currentPage = this.filter.pageInfo.page ?? 1;
      this.createFilterCards();
      localStorage.removeItem(`filterDynamicBuildings|${this.utilityId}`);
    }

    this.loadEmcConfig(this.utilityId);
    this.loadList();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loadEmcConfig(utilityId: number) {
    const entity = SectionDefinitionEntityType.Building;
    this.emcService.getSelectedEmcOptions(entity, Purpose.Card, utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => { this.cardEmc = r.body?.data ?? []; },
    });
    this.emcService.getSelectedEmcOptions(entity, Purpose.ListPage, utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.listPageEmc = (r.body?.data ?? []).sort((a: EmcOptions, b: EmcOptions) => a.displayOrder - b.displayOrder);
        this.buildVisibleColumns();
      },
    });
    this.emcService.getSelectedEmcOptions(entity, Purpose.Filter, utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => { this.filterEmc = r.body?.data ?? []; },
    });
  }

  loadList() {
    this.filter.pageInfo.page = this.currentPage;
    this.filter.pageInfo.pageSize = this.pageSize;

    this.buildingService.getAllDynamic(this.filter).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.tableListPage = data.data ?? new TableListPage();
          this.count = this.tableListPage.count ?? 0;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  pageChange(page: number) {
    this.currentPage = page;
    this.loadList();
  }

  onSearchOutput(obj: EmcSearchOutput) {
    this.filter.searchObject = {
      attributeDefinitionId: obj.attributeDefinitionId ?? null,
      staticPropertyName: obj.staticPropertyName ?? '',
      searchValue: obj.searchValue ?? '',
    };
    this.currentPage = 1;
    this.createFilterCards();
    this.loadList();
    this.dynamicCards?.refresh();
  }

  openFilterWindow() { this.isFilterWindowOpened = true; }
  closeFilterWindow() { this.isFilterWindowOpened = false; }

  onAddFilter() {
    this.currentPage = 1;
    this.createFilterCards();
    this.loadList();
    this.dynamicCards?.refresh();
  }

  applyFilter() {
    this.currentPage = 1;
    this.loadList();
    this.dynamicCards?.refresh();
  }

  resetFilter() {
    const fresh = new DynamicBuildingFilter();
    this.filter.filterParams = fresh.filterParams;
    this.filter.dynamicFilters = fresh.dynamicFilters;
    this.filter.searchObject = fresh.searchObject;
    this.filter.pageInfo = fresh.pageInfo;
    this.filter.utilityId = this.utilityId;
    this.filter.pageInfo.pageSize = this.pageSize;
    this.checklistItems = [];
    this.currentPage = 1;
    this.loadList();
    this.dynamicCards?.refresh();
  }

  removeFilter(item: ChecklistItem) {
    const fp = this.filter.filterParams;
    switch (item.property) {
      case 'Search':
        this.filter.searchObject = { attributeDefinitionId: null, staticPropertyName: '', searchValue: '' };
        break;
      case 'Validation Status':
        fp.validationStatuses = fp.validationStatuses.filter(s => (this.validationStatusLabels[s] ?? s.toString()) !== item.selectedValue);
        break;
      case 'Review Status':
        fp.reviewStatus = null;
        break;
      case 'Tag':
        fp.tag = null;
        break;
      case 'Date From':
        fp.dateFrom = null;
        break;
      case 'Date To':
        fp.dateTo = null;
        break;
      case 'Region':
        fp.selectedRegionModel = null;
        fp.selectedRegions = [];
        fp.selectedAreaModels = [];
        fp.selectedAreas = [];
        break;
      case 'District':
        fp.selectedAreaModels = fp.selectedAreaModels.filter(a => a.name !== item.selectedValue);
        fp.selectedAreas = fp.selectedAreaModels.map(a => a.id);
        break;
      case 'Geographical Region':
        fp.selectedGeoRegionModel = null;
        fp.selectedGeographicalRegions = [];
        fp.selectedSubGeoModels = [];
        fp.selectedSubgeographicalRegions = [];
        break;
      case 'Sub-Geographical Region':
        fp.selectedSubGeoModels = fp.selectedSubGeoModels.filter(s => s.name !== item.selectedValue);
        fp.selectedSubgeographicalRegions = fp.selectedSubGeoModels.map(s => s.id);
        break;
      case 'User':
        fp.selectedUserModels = fp.selectedUserModels.filter(u => u.name !== item.selectedValue);
        fp.selectedUsers = fp.selectedUserModels.map(u => u.id);
        break;
    }
    this.createFilterCards();
    this.applyFilter();
  }

  private createFilterCards() {
    const items: ChecklistItem[] = [];
    const fp = this.filter.filterParams;

    if (this.filter.searchObject.searchValue) {
      items.push(new ChecklistItem('Search', this.filter.searchObject.searchValue, 'COMMON.SEARCH', this.filter.searchObject.searchValue));
    }
    if (fp.dateFrom) items.push(new ChecklistItem('Date From', fp.dateFrom, 'COMMON.DATE_FROM', fp.dateFrom));
    if (fp.dateTo) items.push(new ChecklistItem('Date To', fp.dateTo, 'COMMON.DATE_TO', fp.dateTo));
    fp.validationStatuses.forEach(s => {
      const label = this.validationStatusLabels[s] ?? s.toString();
      items.push(new ChecklistItem('Validation Status', label, 'COMMON.VALIDATION_STATUS', label));
    });
    if (fp.reviewStatus != null) {
      const label = fp.reviewStatus ? 'Reviewed' : 'Not Reviewed';
      items.push(new ChecklistItem('Review Status', label, 'COMMON.REVIEW_STATUS', label));
    }
    if (fp.tag != null) {
      const label = fp.tag ? 'Inaccessible' : 'Tagged';
      items.push(new ChecklistItem('Tag', label, 'COMMON.TAG.TITLE', label));
    }
    if (fp.selectedRegionModel) {
      items.push(new ChecklistItem('Region', fp.selectedRegionModel.name, 'COMMON.BU.SINGLE', fp.selectedRegionModel.name));
    }
    fp.selectedAreaModels.forEach(a => items.push(new ChecklistItem('District', a.name, 'COMMON.UT.SINGLE', a.name)));
    if (fp.selectedGeoRegionModel) {
      items.push(new ChecklistItem('Geographical Region', fp.selectedGeoRegionModel.name, 'Geographical Region', fp.selectedGeoRegionModel.name));
    }
    fp.selectedSubGeoModels.forEach(s => items.push(new ChecklistItem('Sub-Geographical Region', s.name, 'Sub-Geographical Region', s.name)));
    fp.selectedUserModels.forEach(u => items.push(new ChecklistItem('User', u.name, 'COMMON.USER.SINGLE', u.name)));

    this.checklistItems = items;
  }

  downloadBuildingInfo() {
    this.showDownload = false;
    const downloadFilter: DynamicBuildingFilter = { ...this.filter, pageInfo: { page: -1, pageSize: -1 } };
    this.buildingService.downloadFileDynamic(downloadFilter).subscribe({
      next: (response) => this.handleDownloadResponse(response.body),
      error: () => this.toastr.error('Request failed'),
    });
  }

  downloadInaccessibleMeters() {
    this.showDownload = false;
    this.buildingService.downloadInaccessible({ ...this.filter.filterParams }).subscribe({
      next: (response) => this.handleDownloadResponse(response.body),
      error: () => this.toastr.error('Request failed'),
    });
  }

  downloadReplacedSlrn() {
    this.showDownload = false;
    this.buildingService.downloadReplacedSlrn({ ...this.filter.filterParams }).subscribe({
      next: (response) => this.handleDownloadResponse(response.body),
      error: () => this.toastr.error('Request failed'),
    });
  }

  private handleDownloadResponse(data: any) {
    if (data?.status === 'OK' || data?.status === '200') window.location.href = data.data;
    else if (data?.status === 'Pending' || data?.status === '600') this.toastr.info(data?.message || '');
    else this.toastr.error(data?.message || 'Request failed');
  }

  onRowClick(guid: string) {
    if (!guid) return;
    localStorage.setItem(`filterDynamicBuildings|${this.utilityId}`, JSON.stringify(this.filter));
    localStorage.setItem('cameFrom', 'Building');
    this.router.navigate([`/buildings/edit/${guid}`]);
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.showDownload = false;
  }

  private buildVisibleColumns() {
    this.visibleColumns = this.listPageEmc
      .map((emc, index) => ({ emc, index }))
      .filter(({ emc }) => this.isColumnVisible(emc.staticPropertyName))
      .map(({ emc, index }) => ({ label: emc.labelName, index }));
  }

  private isColumnVisible(staticName: string | null): boolean {
    if (!staticName) return true;
    switch (staticName) {
      case 'ElectricalAddressCode':
        return this.permissionsService.isBuildingElectricalAddressCodeAllowed;
      case 'PropertyCode':
        return this.permissionsService.isBuildingPropertyCodeAllowed;
      case 'ValidationStatus':
        return this.permissionsService.isValidationStatusAllowed;
      case 'ReviewStatus':
        return this.permissionsService.isReviewStatusAllowed;
      default:
        return true;
    }
  }
}
